import { AppSidebar } from '@neo/blocks/sidebar/app-sidebar'
import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'router-v6'
import { REGIONS } from 'v2/configs/constants'
import { getDefaultRoute } from 'v2/configs/routes'
import useFavouritesAndViews from 'v2/pages/conversations/hooks/use-get-favourites-views'
import { useFavoriteViewMutation } from 'v2/pages/conversations/mutations/conversations'
import {
  useActiveConversationsQuery,
  useInvitedContactsQuery,
  useViewsQuery,
} from 'v2/pages/conversations/queries'
import { useAllTasksQuery, useUnassignedTasksQuery } from 'v2/pages/conversations/tasks/queries'
import { useUsageReportsGate } from 'v2/pages/reports/usage-reports/hooks/use-usage-reports-gate'
import { useCurrentStatusQuery } from 'v2/pages/settings/org-status/queries'
import {
  useOrganizationsQuery,
  useProfileQuery,
  useSwitchOrganizationMutationV2,
} from 'v2/pages/settings/queries'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { usePermissions } from 'v2/shared/hooks/use-permissions'
import { useUserPlan } from 'v2/shared/hooks/use-plan'
import { useRoutes } from 'v2/shared/hooks/use-routes'
import { useAppStore } from 'v2/state/app'
import { TaskViewName } from 'v2/types'
import { UserRole } from 'v2/types/api/auth'
import type { ROrganizationLite } from 'v2/types/api/settings'
import { RbacModules, RbacPermissionTypes } from 'v2/types/rbac'
import { getLocalAuth, sessionHasRole } from 'v2/utils'
import { CreateOrganizationModal } from './create-organization-modal'
import { resolveFreeTrialRegionLock } from './create-organization-modal/free-trial-region-lock'
import { StatusMenu } from './status-menu'

interface AppSidebarWrapperProps
  extends Omit<
    React.ComponentProps<typeof AppSidebar>,
    'avatarProps' | 'viewsData' | 'handleSwitchOrganization'
  > {}

export const AppSidebarWrapper: React.FC<AppSidebarWrapperProps> = (props) => {
  const navigate = useNavigate()
  const { data: profileData } = useProfileQuery()
  const { data: organizations } = useOrganizationsQuery()
  const { data: agentStatusData } = useCurrentStatusQuery()
  const { data: views } = useViewsQuery()
  const { favourites, views: publicViews } = useFavouritesAndViews()
  const routes = useRoutes() // Get filtered routes based on user permissions
  const showDuplicateSidebar = useAppStore((state) => state.showDuplicateSidebar)
  const [isCreateOrgModalOpen, setIsCreateOrgModalOpen] = useState(false)

  // All conversation data from sub-nav.tsx
  const { data: activeConversations } = useActiveConversationsQuery(
    (c) => c?.filter((conv) => !conv.agent_secondary_conversation_state)?.length,
  )
  const { data: invitedContacts } = useInvitedContactsQuery((c) => c?.length)
  const authDetails = getLocalAuth()
  const { data: tasksData } = useAllTasksQuery(TaskViewName.AllTasks)
  const { data: myTasksData } = useAllTasksQuery(TaskViewName.MyTasks)
  const { data: unassignedTasks } = useUnassignedTasksQuery()
  const { data: exceptionsTasks } = useAllTasksQuery(TaskViewName.Exceptions)
  const { mutate: favouriteView } = useFavoriteViewMutation()
  const { hasPermission } = usePermissions()
  const { isFreePlan } = useUserPlan()
  const { mutate: switchOrganization } = useSwitchOrganizationMutationV2()
  const { voiceAgentsSplitEnabled } = useFeatureFlags()
  const setShowDuplicateSidebar = useAppStore((state) => state.setShowDuplicateSidebar)
  const setSwitchingToOrgName = useAppStore((state) => state.setSwitchingToOrgName)

  const humanSpecialistEnabled = profileData?.organizations?.[0]?.human_specialist_enabled ?? false

  const avatarProps = {
    data: {
      name: `${profileData?.account_first_name} ${profileData?.account_last_name}`,
      email: profileData?.email,
    },
    organizations,
    logoutAction: window.cx.logout,
    statusColor: humanSpecialistEnabled ? agentStatusData?.color : undefined,
    statusMenu: humanSpecialistEnabled ? <StatusMenu /> : undefined,
    orgUuid: profileData?.organizations?.[0]?.org_id,
  }

  const conversationData = {
    favourites,
    publicViews,
    hasViews: Boolean(views),
    // Conversation counts and data
    activeConversations: activeConversations ?? 0,
    invitedContacts: invitedContacts ?? 0,
    tasksData: tasksData?.total_count ?? 0,
    myTasksData: myTasksData?.total_count ?? 0,
    unassignedTasks: unassignedTasks?.total_count ?? 0,
    exceptionsTasks: exceptionsTasks?.total_count ?? 0,
    // User permissions
    isHumanAgent: profileData?.organizations[0]?.is_human_agent,
    hasLiveConversationPermission: hasPermission(
      RbacModules.liveConversation,
      RbacPermissionTypes.view,
    ),
    // Functions
    favouriteView,
    authDetails,
  }

  const handleSwitchOrganization = useCallback(
    (org: ROrganizationLite) => {
      setSwitchingToOrgName(org.org_name)
      switchOrganization({ org_uuid: org.org_uuid, region: org.region })
    },
    [switchOrganization, setSwitchingToOrgName],
  )

  const handleCreateOrganization = useCallback(() => {
    setIsCreateOrgModalOpen(true)
  }, [])

  const handleLogoClick = useCallback(() => {
    navigate(getDefaultRoute())
  }, [navigate])

  const isAdmin = sessionHasRole(UserRole.administrator)

  const existingOrgNames = useMemo(
    () => organizations?.map((org) => org.org_name) ?? [],
    [organizations],
  )

  // Free trial allows one org per data region (hodor MaxOrgsPerRegionByPlan).
  // Steer free-trial admins to the region they don't yet occupy and lock it;
  // adding another org in the same region requires an upgrade. See SER-5737.
  const freeTrialRegionLock = useMemo(
    () =>
      resolveFreeTrialRegionLock(
        organizations ?? [],
        isFreePlan,
        getLocalAuth().region || REGIONS.US,
      ),
    [isFreePlan, organizations],
  )

  const isHumanSpecialistEnabled =
    profileData?.organizations?.[0]?.human_specialist_enabled ?? false
  const hasFlowPermission = hasPermission(RbacModules.aiAgent, RbacPermissionTypes.view)
  const usageEnabled = useUsageReportsGate()
  const reportsConfig = useMemo(
    () => ({
      isHumanSpecialistEnabled,
      hasFlowPermission,
      voiceAgentsSplit: voiceAgentsSplitEnabled,
      usageEnabled,
    }),
    [isHumanSpecialistEnabled, hasFlowPermission, voiceAgentsSplitEnabled, usageEnabled],
  )

  return (
    <>
      <AppSidebar
        avatarProps={profileData ? avatarProps : undefined}
        viewsData={conversationData}
        routes={routes} // Pass filtered routes to sidebar
        handleSwitchOrganization={handleSwitchOrganization}
        showDuplicateSidebar={showDuplicateSidebar}
        setShowDuplicateSidebar={setShowDuplicateSidebar}
        isAdmin={isAdmin}
        onCreateOrganization={isAdmin ? handleCreateOrganization : undefined}
        onLogoClick={handleLogoClick}
        reportsConfig={reportsConfig}
        {...props}
      />
      {isAdmin && (
        <CreateOrganizationModal
          open={isCreateOrgModalOpen}
          onOpenChange={setIsCreateOrgModalOpen}
          existingOrgNames={existingOrgNames}
          freeTrialRegionLock={freeTrialRegionLock}
        />
      )}
    </>
  )
}
